// ─────────────────────────────────────────────
//  useBreakBoardRows — the provider rows the break boards render.
//
//  Today's rostered CRNAs minus departed ones, each carrying the room they are
//  working and that room's live break-channel record (or a fresh all-"none"
//  record when they genuinely have no room). The web Break Engine and the
//  mobile Break tab both render from this ONE derivation, so the two boards
//  cannot disagree about who is on the list or what colour a cell is.
//
//  ⚠️ WHO IS IN WHICH ROOM COMES FROM THE ASSIGNMENT ROWS, NOT THE ROOM DOCS.
//  This matched only `room.providerId === member.id`, which is written by the
//  grid — but a board built by SHEET IMPORT staffs the assignment rows and
//  leaves the room docs unstaffed. Every provider therefore resolved to no
//  room, and because the break record lives ON the room doc, they also all got
//  a fresh all-"none" record: the board showed the whole roster as "No room"
//  with four dead grey cells, on a day when people were actually on breaks.
//  (Same trap, same fix, as nextRoomChoices' occupancy.)
//
//  The room-doc match is kept as a FALLBACK so grid-built boards, and rooms
//  staffed by a status change rather than a row, still resolve.
//
//  `breakBoardRows` is the pure builder (house pattern: unit-testable with
//  plain fixtures). DailyAssignmentPage calls the pure form because its roster
//  inputs are computed after an early access-guard return, where a hook call
//  would break the rules of hooks. Mobile screens use the memo wrapper.
// ─────────────────────────────────────────────
import { useMemo } from "react";
import { initialBreaks, normalizeBreakChannel, lastMealAtMin, mealGap, byMealNeed } from "../domain/breaks";
import { ALERT_TYPES, isLiveAlert } from "../domain/alerts";
import { roomForOrRoom } from "../domain/gridTemplate";
import { rowClaimsAt, rowStationsAt, isAllDaySentinel, hmToMinutes } from "../domain/timeline";
import { mergeSecondProviders, roomSeatName, secondProviderOf } from "../domain/secondProvider";

// Roster names are "Josh Larson"; the sheet importer writes assignment names as
// "Josh Larson, CRNA". Compare on the part before the role, honorific-insensitive
// — the same normalisation domain/myRooms uses, and for the same reason.
const nameKey = (s) =>
  String(s || "")
    .split(",")[0]
    .trim()
    .toLowerCase()
    .replace(/^dr\.?\s+/, "");

// The day's row for this person AS THE PERSON IN THE ROOM. A staged relief
// (crnaReliefId) is deliberately NOT matched: until the takeover is confirmed
// they are not in that room, and claiming they are would show one CRNA in two
// places on the board.
//
// ⚠️ AND IT MUST BE THE ROW THAT STILL CLAIMS THEM, NOT THE FIRST ONE FOUND.
// `.find()` returns whichever row sits earliest in the array, so a CRNA whose
// morning room finished at 11:00 and who is in a second room until 22:30
// resolved to the FINISHED one. Filtering on that row would have dropped them
// off the break board while they stand in a running room — the dangerous
// direction, on the one screen a colleague checks to find cover. So every
// candidate is gathered in the same priority order and the first one that still
// claims at `nowMin` wins; only when NONE of their rows claims are they free.
//
// `nowMin` null (no clock handed in) makes `rowClaimsAt` true for everything,
// so the first candidate wins exactly as before.
function assignmentCandidates(member, assignments) {
  const list = Array.isArray(assignments) ? assignments : [];
  const key = nameKey(member?.name);
  const out = [];
  const push = (a) => { if (a && !out.includes(a)) out.push(a); };
  if (member?.id) list.filter((a) => a && a.ahpId === member.id).forEach(push);
  if (key) list.filter((a) => a && nameKey(roomSeatName(a)) === key).forEach(push);
  // ⚠️ AN ORIENTEE IS IN THE ROOM TOO — matched after the seat holder, so the
  // person HOLDING the seat still wins when both resolve to the same member.
  if (member?.id) {
    list.filter((a) => { const o = secondProviderOf(a); return o && o.id === member.id; }).forEach(push);
  }
  if (key) {
    list.filter((a) => { const o = secondProviderOf(a); return o && nameKey(o.name) === key; }).forEach(push);
  }
  return out;
}

// WHEN THIS PERSON'S DAY STARTED — the earliest start across every row they
// hold today, not the case they happen to be in now.
//
// ⚠️ THE CURRENT ROW IS THE WRONG ANSWER. Somebody who finished one case at
// 11:00 and picked up another at 11:15 has been working since 07:00, and
// measuring from the second row would report them as two hours fresher than
// they are — on the one list that exists to find whoever needs a break most.
// ⚠️ A STANDING ON-CALL WATCH IS NOT TIME STUCK IN A ROOM. Reported live on
// 2026-09-11: the OB on-call CRNA sat in FREE NOW — the board's own list of who
// is available — while this card said "in room since 07:00 · no meal break ·
// 34m" and ranked her as needing a break. Both cannot be true.
//
// `benchProviders` has always known the difference: a row that `isStandingOnCall`
// does NOT mark somebody assigned, because a watch is availability, not a case.
// This function counted its start time anyway, so the one list built to find who
// is trapped and unfed was ranking somebody who was free to walk to the cafeteria.
//
// Skipping it usually leaves NO row, and that is the right answer: `mealGap`
// then has nothing to measure from and the line stays silent, rather than
// inventing an hour. The moment a real case starts, the clock starts with it.
function onSinceMinFor(member, assignments) {
  let earliest = null;
  for (const a of assignmentCandidates(member, assignments)) {
    // ⚠️ THE SENTINEL, NOT THE CALL FLAG. This skipped `isStandingOnCall`, which
    // is `isOnCall AND caseless` — so it stopped counting the OB watch's 07:00
    // and went on counting every OTHER row carrying the same empty
    // `07:00 → 07:00` window. Live, 2026-09-13, the moment that flag was
    // narrowed to real call codes: Titilope Somade's row read "No room" and
    // "in room since 07:00 · no meal break · 7h52m" on the same card. The row
    // has no case on it; there is no time in a room to count.
    if (isAllDaySentinel(a)) continue;
    const start = hmToMinutes(a?.startTime);
    if (start == null) continue;
    if (earliest == null || start < earliest) earliest = start;
  }
  return earliest;
}


// EVERY room this person has held today, with the window they held it.
//
// ⚠️ A BREAK IS STAMPED ON THE ROOM DOC, NOT ON THE PERSON. So the lunch a CRNA
// ate at 11:30 in WOR 5 is invisible from the WOR 9 they moved to at 13:00, and
// the card called them "no meal break · 8h" — sorting somebody who ate three
// hours ago to the TOP of a list whose whole purpose is finding who has not
// eaten. `onSinceMinFor` already scans every row; the meal lookup did not, so
// the two halves of the same subtraction ran at different scopes.
//
// ⚠️ AND THE WINDOW IS LOAD-BEARING. Rooms outlive the people in them: once Josh
// leaves WOR 5 at 13:00, the next CRNA's 14:00 lunch lands on that SAME room
// doc. Reading the room's meal record without asking whether the person was
// still in it would hand Josh somebody else's lunch and report him fed more
// recently than he is — the same false claim, pointed the more dangerous way,
// because it sorts a hungry provider DOWN.
function mealRoomsFor(member, assignments, roomList) {
  const out = [];
  for (const a of assignmentCandidates(member, assignments)) {
    const room = a?.orRoom ? roomForOrRoom(a.orRoom, roomList) : null;
    if (!room) continue;
    out.push({
      room,
      // The ROW's identity, so the "they are still on this one" widening can be
      // pinned to a row rather than to a room a person may hold more than once.
      rowId: a?.id || null,
      startMin: hmToMinutes(a?.startTime),
      endMin: hmToMinutes(a?.endTime),
    });
  }
  return out;
}

// Was this person the one standing in that room when the meal was stamped?
//
// ⚠️ FAILS CLOSED, ON BOTH SIDES. This used to constrain only the side it knew,
// which meant a row missing `endTime` credited every LATER meal on that room doc
// and a row missing `startTime` credited the previous shift's. Both are the
// dangerous direction: they hand somebody another person's meal, and that sorts
// a hungry clinician DOWN the one list built to find them.
//
// ⚠️ THE MODEL CANNOT ALWAYS ANSWER THIS, AND THAT IS THE POINT. `room.breaks`
// has no per-person key — a meal is stamped on the ROOM — so attribution is
// inferred from the hours somebody held it. When either edge of that window is
// unknown the inference is not available, and the honest answer is "no meal
// recorded", not a guess. Over-reporting hunger is the safe direction; this
// board exists to find people who have not eaten, so showing somebody as hungry
// who has eaten costs a question, while the reverse costs them their break.
function heldRoomAt(atMin, startMin, endMin) {
  if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return false;
  return atMin >= startMin && atMin <= endMin;
}

// When did somebody else take this room over? The claiming row is extended to
// `nowMin` so a case running past its scheduled end still counts — but
// `rowClaimsAt` keeps a provider on this board for STAFF_BUFFER_MIN past that
// end, so for those minutes the outgoing and the incoming provider both map to
// the room and the successor's meal was inheritable. Their row's start is the
// handover time the model does have.
function successorStartMin(assignments, claimingRow) {
  const orRoom = claimingRow?.orRoom;
  const mine = claimingRow?.id || null;
  if (!orRoom) return null;
  const from = hmToMinutes(claimingRow?.startTime);
  let next = null;
  for (const a of Array.isArray(assignments) ? assignments : []) {
    if (!a || a.orRoom !== orRoom) continue;
    if (mine && a.id === mine) continue;
    const st = hmToMinutes(a?.startTime);
    if (st == null) continue;
    if (Number.isFinite(from) && st <= from) continue;
    if (next == null || st < next) next = st;
  }
  return next;
}

// The meal-gap facts for one row. Kept here rather than in domain/breaks
// because only this builder knows which assignment rows belong to a member;
// the arithmetic itself is the domain's.
function mealNeed(member, assignments, roomList, room, claimingRow, breaks, breakTimes, nowMin) {
  const onSinceMin = onSinceMinFor(member, assignments);
  const held = mealRoomsFor(member, assignments, roomList);

  // ⚠️ THE ROW THEY ARE ON NOW RUNS UNTIL NOW — NOT UNTIL ITS SCHEDULED END, AND
  // NOT FOREVER. `endTime` is the SCHEDULED end, so bounding by it rejects the
  // lunch of a CRNA whose case ran long and who ate at 15:30 in a room booked
  // until 15:00 — reported "no meal break" and sorted to the TOP of the hunger
  // list, the list's purpose inverted.
  //
  // ⚠️ BUT THE WIDENING BELONGS TO ONE ROW, NOT TO A ROOM ID. Matching on the
  // room reopened the predecessor bug by the back door: somebody who works WOR 5
  // in the morning, WOR 9 at midday and RETURNS to WOR 5 had their MORNING entry
  // widened too, so the interim occupant's 12:30 lunch on that same room doc
  // became theirs. Only the row that actually claims them right now is extended,
  // and only as far as `nowMin` — a meal cannot be stamped in the future, so
  // infinity buys nothing and costs exactly this.
  const claimingId = claimingRow?.id || null;
  // How far the row they are STILL on reaches: to now, but never past the moment
  // somebody else took the room.
  const handover = successorStartMin(assignments, claimingRow);
  const openTo = Number.isFinite(nowMin)
    ? (Number.isFinite(handover) ? Math.min(nowMin, handover) : nowMin)
    : null;
  const rows = held.map((h) =>
    claimingId && h.rowId === claimingId && Number.isFinite(openTo)
      ? { ...h, endMin: Math.max(h.endMin ?? openTo, openTo) }
      : h
  );

  // ⚠️ AND THE CURRENT ROOM MUST BE READ EVEN WHEN NO ROW RESOLVES TO IT. A room
  // doc can staff somebody by `providerId`, or their row can name a room that is
  // not on the board — `resolveRoom` finds it either way. Gating on "has any
  // rows at all" discarded that room's real meal whenever the person happened to
  // hold one OTHER room today.
  //
  // ⚠️ STILL LOWER-BOUNDED BY THE ROW THAT CLAIMS THEM. An unbounded read here
  // hands them a 06:00 lunch the previous shift left on that room doc. When no
  // row claims them at all there is nothing to bound it with, which is the trade
  // a grid-built board has always made.
  const currentId = room?.id || null;
  const currentCovered = currentId && rows.some((h) => h.room?.id === currentId);
  if (!currentCovered) {
    rows.push({
      room: { breaks, breakTimes },
      startMin: hmToMinutes(claimingRow?.startTime),
      endMin: openTo,
    });
  }

  // ⚠️ THE ONE PLACE A WINDOWLESS READ IS STILL HONEST: A BOARD WITH NO ROWS AT
  // ALL. A grid-built board staffs its rooms by `providerId` and carries no
  // assignment rows, so there is no window information for ANYBODY — and equally
  // no room change for anybody, which is the only thing the window defends
  // against. One room, one holder, all day: the stamp on that doc is theirs.
  //
  // ⚠️ IT LEANS ON THE NIGHTLY RESET. What this cannot rule out is yesterday's
  // stamp, since `breaks` is day-scoped and cleared overnight by `clearRoom`. If
  // that reset ever fails, this is the line that will quietly credit a meal from
  // the previous day — everywhere else, a window would have caught it.
  const noRowsAtAll = !held.length && !claimingRow;

  // ONE assignment, so the two paths can never drift into different answers —
  // which is the exact shape of mistake this function has already made.
  let lastMealMin = null;
  if (noRowsAtAll) {
    lastMealMin = lastMealAtMin(breaks, breakTimes);
  } else {
    for (const { room: r, startMin, endMin } of rows) {
      const at = lastMealAtMin(r?.breaks, r?.breakTimes);
      if (at == null || !heldRoomAt(at, startMin, endMin)) continue;
      if (lastMealMin == null || at > lastMealMin) lastMealMin = at;
    }
  }
  const gap = mealGap({ onSinceMin, lastMealMin, nowMin });
  return {
    onSinceMin,
    lastMealMin,
    // null when the board cannot know — never 0, which would sort the person it
    // knows least about to the bottom of "who needs a break".
    mealGapMin: gap ? gap.minutes : null,
    mealGapLabel: gap ? gap.label : null,
    hadMeal: gap ? gap.hadMeal : false,
  };
}

function assignmentFor(member, assignments, nowMin = null) {
  const candidates = assignmentCandidates(member, assignments);
  return candidates.find((a) => rowStationsAt(a, nowMin)) || null;
}

/** Did this person have rows today, none of which still claims them? */
function allRoomsFinished(member, assignments, nowMin) {
  if (nowMin == null) return false;
  const candidates = assignmentCandidates(member, assignments);
  return candidates.length > 0 && !candidates.some((a) => rowClaimsAt(a, nowMin));
}


// ⚠️ A REQUESTED break does NOT live on the room doc.
// `requestMyBreak` only raises a BREAK_REQUEST alert (useRoomBreakActions) — it
// never writes "requested" into room.breaks. The board derives its cells purely
// from that record, so it was structurally blind to requests: a CRNA could see
// "Lunch break requested" on their own card while the facility board that is
// supposed to show every live break left the cell grey. The one screen a
// colleague checks to find someone to cover showed nothing to cover.
//
// So the live alerts are merged in here, in the SHARED builder, rather than
// patched onto one screen. Only a channel that is otherwise untouched is
// marked: a break already done or in progress outranks a stale request.
function requestedChannelForRoom(roomId, alerts) {
  if (!roomId) return null;
  const hit = (alerts || []).find(
    (a) => a && a.type === ALERT_TYPES.BREAK_REQUEST && isLiveAlert(a) && a.roomId === roomId
  );
  return hit ? normalizeBreakChannel(hit.breakType) : null;
}

// Pure row builder — [{ ...member, roomName, breaks }] for non-departed CRNAs.
export function breakBoardRows(
  rosterCrnas = [],
  departedMap = {},
  rooms = [],
  assignments = [],
  alerts = [],
  nowMin = null
) {
  const roomList = Array.isArray(rooms) ? rooms : [];
  // ⚠️ THE SECOND CRNA IN A ROOM IS OFTEN NOT ON THE ROSTER DOC. This maps over
  // roster members, so a person who exists only as `orienteeName` on an
  // assignment row — every board imported before enrolment shipped — appears on
  // no break board at all. No error, no warning, no row; the one screen a
  // colleague checks to find cover simply omits them. `mergeSecondProviders`
  // derives the missing member from the rows this builder is ALREADY given, and
  // is idempotent, so a board that enrolled them properly gains nothing.
  const members = mergeSecondProviders(rosterCrnas, assignments);
  const resolveRoom = (member) => {
    const row = assignmentFor(member, assignments, nowMin);
    // Assignment row first (the source of truth for staffing), room doc second.
    return (
      (row?.orRoom ? roomForOrRoom(row.orRoom, roomList) : null) ||
      roomList.find((r) => r && r.providerId === member.id) ||
      // A row with a room name we cannot match to a live room doc still tells
      // us WHERE they are — show the name, with no break record to read.
      (row?.orRoom ? { name: row.orRoom, breaks: null } : null) ||
      null
    );
  };

  return members
    .filter((m) => m && !departedMap?.[m.id])
    // ⚠️ A FINISHED ROOM DOES NOT NEED A BREAK. Reported 2026-08-31 at 21:00,
    // with the 7 PM card on the same screen reading `Rooms: 0`: the break board
    // still listed eight people, every one of whose cases had ended hours
    // earlier. This builder had no clock at all, so a row put somebody on the
    // break board until midnight. `rowClaimsAt` is the same predicate the bench,
    // FREE NOW and the departure modal use, so all four agree about when a room
    // lets go of somebody.
    //
    // ⚠️ ONLY WHEN EVERY ONE OF THEIR ROWS HAS FINISHED, and only when a clock
    // was handed in. Somebody with NO row is not dropped: there is no evidence
    // either way, and this is the screen a colleague checks to find cover, so
    // the absent-evidence case fails closed and keeps them.
    .filter((m) => !allRoomsFinished(m, assignments, nowMin))
    .map((member) => {
      const room = resolveRoom(member);
      // The row that claims them RIGHT NOW — the same one `resolveRoom` used.
      // The meal window widens that row and no other; see `mealNeed`.
      const row = assignmentFor(member, assignments, nowMin);
      const roomBreaks = room?.breaks || initialBreaks();
      const requested = requestedChannelForRoom(room?.id, alerts);
      // A break already done or in progress outranks a lingering request.
      const untouched = !roomBreaks[requested] || roomBreaks[requested] === "none";
      const breaks =
        requested && untouched
          ? { ...roomBreaks, [requested]: "requested" }
          : roomBreaks;
      return {
        ...member,
        // ⚠️ THE ROOM'S ID, AND WITHOUT IT THE CARD IS INERT. TimelineBreaksCard
        // gates its B/L/D/WC chips on `Boolean(row.roomId && onOpenRoom)`, and
        // this builder returned the room's NAME and not its id — so every chip
        // on that card has been `disabled` since it shipped, while the card's
        // own note said "open a room to change one". Verified in a live browser
        // on 2026-09-10: all four chips `disabled: true`.
        //
        // ⚠️ AND THE SUITE COULD NOT SEE IT. `TimelineBreaksCard.test.js` builds
        // its own rows and never sets `roomId`, so it asserted the disabled
        // rendering and passed. `useControlPanelData` re-derives the room from
        // the NAME (`breakRowByRoomId`) precisely because the id was dropped
        // here — a second lookup that exists only to undo this omission.
        roomId: room?.id || "",
        roomName: room?.name || "",
        breaks,
        // WHO is covering them right now. The board named the person on break
        // but never the colleague standing in their room, which is the half of
        // "who is where" a charge nurse actually needs during a break.
        relieverName: room?.relieverName || "",
        // WHEN this channel last changed hands ("HH:MM"). Carried so a surface
        // can say how long a named cover has been on their way — a state that
        // can sit forever with nothing counting is where things go to be
        // forgotten. See `lateCoverItems` in domain/controlPanel.
        breakTimes: room?.breakTimes || null,
        // HOW LONG SINCE THEY ATE — the three facts that were already on this
        // board and had never been put together. See domain/breaks.mealGap.
        ...mealNeed(member, assignments, roomList, room, row, breaks, room?.breakTimes, nowMin),
      };
    })
    // ⚠️ SORTED BY NEED, IN THE SHARED BUILDER. The attending asked for this
    // list "sorted by need", and it belongs here rather than in either card:
    // the web BREAKS card and the phone break board are the same board, and a
    // sort applied in one of them is the exact drift that made "synced
    // everywhere" untrue for the break logic before. Longest gap first, and the
    // rows the board cannot measure park at the end — unknown, not fine.
    .sort(byMealNeed);
}

export function useBreakBoardRows(rosterCrnas, departedMap, rooms, assignments, alerts, nowMin = null) {
  return useMemo(
    () => breakBoardRows(rosterCrnas, departedMap, rooms, assignments, alerts, nowMin),
    [rosterCrnas, departedMap, rooms, assignments, alerts, nowMin]
  );
}
